/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { useMemo } from 'react';

import { icons } from '@/main/assets';
import { ImgStyled } from '@/main/components/common/Block/Img';
import { EFormatData } from '@/main/pages/PageDashboard/types/widgetTypes';
import { IPageNewsTranslation } from '@modules/internationalization/types';
import { TableCellDate, TableHeader, TableCellText, TableCellIconButton, TTableColumnConfig } from '@modules/table';

import { ENewsTableFileds } from '../constants';
import { ITableNews } from '../types';

export const useTableNewsConfig = (
    { table }: IPageNewsTranslation,
    handleDeleteItem: (id: string, title: string) => void,
): TTableColumnConfig<ITableNews> => {
    return useMemo(
        () => ({
            /* id */
            [ENewsTableFileds.ID]: {
                accessor: ENewsTableFileds.ID,
                Header: <TableHeader label={table[ENewsTableFileds.ID].header} name={ENewsTableFileds.ID} />,
                disableResizing: true,
                width: 64,
                minWidth: 64,
                ...TableCellText<ITableNews>(({ id }) => id),
            },
            /* Тип */
            [ENewsTableFileds.TYPE]: {
                accessor: ENewsTableFileds.TYPE,
                Header: <TableHeader label={table[ENewsTableFileds.TYPE].header} name={ENewsTableFileds.TYPE} />,
                disableResizing: true,
                width: 120,
                minWidth: 120,
                ...TableCellText<ITableNews>(({ type }) => type),
            },
            /* Заголовок */
            [ENewsTableFileds.TITLE]: {
                accessor: ENewsTableFileds.TITLE,
                Header: <TableHeader label={table[ENewsTableFileds.TITLE].header} name={ENewsTableFileds.TITLE} />,
                disableResizing: true,
                width: 310,
                minWidth: 310,
                ...TableCellText<ITableNews>(({ title }) => title),
            },
            /* Дата  публикации с */
            [ENewsTableFileds.PUBLISH_FROM]: {
                accessor: ENewsTableFileds.PUBLISH_FROM,
                Header: (
                    <TableHeader
                        label={table[ENewsTableFileds.PUBLISH_FROM].header}
                        name={ENewsTableFileds.PUBLISH_FROM}
                    />
                ),
                width: 150,
                minWidth: 150,
                disableResizing: true,
                ...TableCellDate<ITableNews>(({ publishFrom }) => ({
                    date: publishFrom,
                    format: EFormatData.FullData,
                    enableSubtitle: false,
                })),
            },
            /* Дата публикации по */
            [ENewsTableFileds.PUBLISH_TO]: {
                accessor: ENewsTableFileds.PUBLISH_TO,
                Header: (
                    <TableHeader label={table[ENewsTableFileds.PUBLISH_TO].header} name={ENewsTableFileds.PUBLISH_TO} />
                ),
                width: 150,
                minWidth: 150,
                disableResizing: true,
                ...TableCellDate<ITableNews>(({ publishTo }) => ({
                    date: publishTo,
                    format: EFormatData.FullData,
                    enableSubtitle: false,
                })),
            },
            /* Доступность */
            [ENewsTableFileds.FOR_ALL]: {
                accessor: ENewsTableFileds.FOR_ALL,
                Header: <TableHeader label={table[ENewsTableFileds.FOR_ALL].header} name={ENewsTableFileds.FOR_ALL} />,
                disableResizing: true,
                width: 106,
                minWidth: 106,
                ...TableCellText<ITableNews>(({ forAll }) => forAll),
            },
            /* Дата создания */
            [ENewsTableFileds.CREATED]: {
                accessor: ENewsTableFileds.CREATED,
                Header: <TableHeader label={table[ENewsTableFileds.CREATED].header} name={ENewsTableFileds.CREATED} />,
                disableResizing: true,
                width: 150,
                minWidth: 150,
                ...TableCellDate<ITableNews>(({ created }) => ({
                    date: created,
                    format: EFormatData.FullData,
                    enableSubtitle: false,
                })),
            },
            /* Автор */
            [ENewsTableFileds.AUTHOR]: {
                accessor: ENewsTableFileds.AUTHOR,
                Header: <TableHeader label={table[ENewsTableFileds.AUTHOR].header} name={ENewsTableFileds.AUTHOR} />,
                disableResizing: true,
                width: 110,
                minWidth: 110,
                ...TableCellText<ITableNews>(({ author }) => author),
            },
            /* Удалить */
            [ENewsTableFileds.DELETE]: {
                accessor: ENewsTableFileds.DELETE,
                align: 'right',
                disableResizing: true,
                width: 40,
                minWidth: 40,
                ...TableCellIconButton<ITableNews>(({ id, title }) => ({
                    children: <ImgStyled className="small icon__close" src={icons.cancelMiddleIcon} />,
                    onClick: () => {
                        handleDeleteItem(id, title);
                    },
                })),
            },
        }),
        [handleDeleteItem, table],
    );
};
